import { Component, OnInit, OnDestroy } from '@angular/core';
import { NgbModal, NgbNavChangeEvent } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { ConfigService } from '../../services/config.service';
import { AlertsService } from '../../services/alerts.service';
import { AlbumsService } from '../../services/albums.service';
import { ConfirmComponent } from '../../components/confirm/confirm.component';
import { AlbumComponent } from '../../albums/album/album.component';
import { AlbumShareComponent } from '../../albums/album-share/album-share.component';
import { UserService } from '../../services/user.service';
import { SearchService } from '../../search/services/search.service';
import { RoutingService } from '../../services/routing.service';

/**
 * Main albums management component.
 */
@Component({
	selector: 'albums',
	templateUrl: './albums.component.html',
	styleUrls: [
	  './albums.component.css'
	]	
})
export class AlbumsComponent implements OnInit, OnDestroy {
	
	private userServiceSubscription!: Subscription;
	
	LOCALIZATION: any;
	userPermissions: { [key: string]: any } = {};

	albums: any[] = [];
	shared: any[] = [];
	sharedalbums: boolean = true;

	pagination = {
		page: 1,
		elementsPerPage: 8,
		elementsPerPageSizes: [8, 16, 30],
		sorting: {
			sort: 'created,desc',
			description: 'Newest'
		},
		totalElements: 0,
		pageChanged: (newPage: any) => {
			this.pagination.page = newPage;
			this.loadAlbums(this.pagination.page);
		}
	};
	
	sharedPagination = {
		page: 1,
		elementsPerPage: 8,
		elementsPerPageSizes: [8, 16, 30],
		sorting: {
			sort: 'created,desc',
			description: 'Newest'
		},
		totalElements: 0,
		pageChanged: (newPage: any) => {
			this.sharedPagination.page = newPage;
			this.loadSharedAlbums(this.sharedPagination.page);
		}
	};
	
	activeTab = 1;
	fetching: boolean = false;

	constructor(
		private modalService: NgbModal,
		private userService: UserService,
		private searchService: SearchService,
		private configService: ConfigService,
		private alertsService: AlertsService,
		private albumsService: AlbumsService,
		private routingService: RoutingService
	) {
		this.LOCALIZATION = this.userService.LOCALIZATION;
		this.pagination.sorting.description = this.LOCALIZATION.newest;
		this.sharedPagination.sorting.description = this.LOCALIZATION.newest;
		this.userPermissions = this.userService.getUserPermissionsMap();
	}
	
	ngOnInit(): void {
		this.userServiceSubscription = this.userService.broadcastEvent.subscribe((eventId: string) => {
			if ("localizationLoaded" === eventId) {
				this.LOCALIZATION = this.userService.LOCALIZATION;
			} else if ("userPrefsUpdate" === eventId) {
				this.userPermissions = this.userService.getUserPermissionsMap();
			}
		});	
				
		this.sharedalbums = this.configService.getConfig().albums.sharedalbums;
		if (this.userPermissions['ROLE_ALBUM_MANAGEMENT']) {
			this.loadAlbums();
		}
	}
	
	ngOnDestroy(): void {
		if (this.userServiceSubscription) {
			this.userServiceSubscription.unsubscribe();
		}
	}	

	/**
	 * Loads the albums using the current constraints.
	 */
	loadAlbums(page?: number): void {
		this.fetching = true;

		if (page) {
			this.pagination.page = page;
		}
		let index = this.pagination.page - 1;

		this.albumsService.getAlbums(index, this.pagination, {}).then(
			(data: any) => {
				if (!data) {
					this.albums = [];
				} else {
					this.albums = data.content;
					this.pagination.page = data.page.number + 1;
					this.pagination.elementsPerPage = data.page.size;
					this.pagination.totalElements = data.page.totalElements;
				}
				this.fetching = false;
			},
			(err: any) => {
				console.error("Failed to fetch albums: ", err);
				this.albums = [];
				this.fetching = false;
			}
		);
	}
	
	/**
	 * Loads the shared albums using the current constraints.
	 */
	loadSharedAlbums(page?: number): void {
		this.fetching = true;

		if (page) {
			this.sharedPagination.page = page;
		}
		let index = this.sharedPagination.page - 1;

		this.albumsService.getSharedAlbums(index, this.sharedPagination, {}).then(
			(data: any) => {
				if (!data) {
					this.shared = [];
				} else {
					this.shared = data.content;
					this.sharedPagination.page = data.page.number + 1;
					this.sharedPagination.elementsPerPage = data.page.size;
					this.sharedPagination.totalElements = data.page.totalElements;
				}
				this.fetching = false;
			},
			(err: any) => {
				console.error("Failed to fetch shared albums: ", err);
				this.shared = [];
				this.fetching = false;
			}
		);
	}	
	
	/**
	 * Downloads the contents of the album.
	 */	
	downloadAlbum(album: any): void {
		const modalRef = this.modalService.open(ConfirmComponent, {
			size: 'sm'
		});

		modalRef.componentInstance.modal = {
			callerName: 'downloadAlbum',
			album: album,
			title: this.LOCALIZATION.downloadAlbumHeader,
			message: this.LOCALIZATION.downloadAlbumMessage,
			type: 'primary'
		};

		modalRef.result.then(
			(payload) => {
				if (payload.result?.answer === 'ok') {
					this.albumsService.downloadAlbum(album.id).then(
						(response: any) => {
						    this.alertsService.success({
						        message: this.LOCALIZATION.downloadAlbumSuccess
						    });
						    this.loadAlbums(1);
						}, 
						(err: any) => {
							console.error("Failed to download album", err);
						    this.alertsService.error({
						        message: this.LOCALIZATION.downloadAlbumError
						    });
						}
					);
				}
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);		
	}	
	
	/**
	 * Creates a new album.
	 */		
	addAlbum(): void {
		const modalRef = this.modalService.open(AlbumComponent, {
			size: 'sm'
		});

		modalRef.componentInstance.album = {};

		modalRef.result.then(
			(result) => {
				if (result.result?.answer === 'ok') {
					this.loadAlbums(1);
				}
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);		
	}	
	
	/**
	 * Deletes the album.
	 */		
	deleteAlbum(album: any): void {
		const modalRef = this.modalService.open(ConfirmComponent, {
			size: 'sm'
		});

		modalRef.componentInstance.modal = {
			callerName: 'deleteAlbum',
			album: album,
			title: this.LOCALIZATION.deleteAlbumHeader,
			message: this.LOCALIZATION.deleteAlbumMessage,
			type: 'danger'
		};
		
		modalRef.result.then(
			(result) => {
				if (result.result?.answer === 'ok') {
					this.albumsService.deleteAlbum(album.id).then(
						(response: any) => {
						    this.alertsService.success({
						        message: this.LOCALIZATION.deleteAlbumSuccess
						    });
						    this.loadAlbums(1);
						}, 
						(err: any) => {
							console.error("Failed to delete album", err);
						    this.alertsService.error({
						        message: this.LOCALIZATION.deleteAlbumError
						    });
						}
					);
				}
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);		
	}		
	
	/**
	 * Modifies the album properties.
	 */	
	editAlbum(album: any): void {
		const modalRef = this.modalService.open(AlbumComponent, {
			size: 'sm'
		});

		modalRef.componentInstance.album = album;

		modalRef.result.then(
			(result) => {
				if (result.result?.answer === 'ok') {
					this.loadAlbums(1);
				}
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);		
	}		
	
	/**
	 * Synchronizes the contents of the album.
	 */	
	cleanAlbum(album: any): void {
	    this.albumsService.cleanAlbum(album.id).then(
			(response: any) => {
		        this.alertsService.success({
		            message: this.LOCALIZATION.albumCleanSuccess
		        });
		        this.loadAlbums();
		    }, 
			(err: any) => {
				console.error("Failed to clean album", err);
		        this.alertsService.error({
		            message: this.LOCALIZATION.albumCleanError
		        });
		    }
		);		
	}		
	
	/**
	 * Shares the album.
	 */	
	shareAlbum(album: any): void {
		const modalRef = this.modalService.open(AlbumShareComponent, {
			size: 'sm'
		});

		modalRef.componentInstance.album = album;

		modalRef.result.then(
			(result) => {
				if (result.result?.answer === 'ok') {
					this.loadAlbums(1);
				}
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);		
	}	
	
	/**
	 * The album is no longer shared with the current user.
	 */	
	removeShare(album: any): void {
		const modalRef = this.modalService.open(ConfirmComponent, {
			size: 'sm'
		});

		modalRef.componentInstance.modal = {
			callerName: 'removeShare',
			album: album,
			title: this.LOCALIZATION.unshareHeader,
			message: this.LOCALIZATION.unshareMessage,
			type: 'warning'
		};
		
		modalRef.result.then(
			(result) => {
				if (result.result?.answer === 'ok') {
					this.albumsService.removeAlbumShare(album.id, this.userService.user.email ?? undefined).then(
						(response: any) => {
						    this.alertsService.success({
						        message: this.LOCALIZATION.unshareSuccess
						    });
						    this.loadSharedAlbums(1);
						}, 
						(err: any) => {
							console.error("Failed to remove album share", err);
						    this.alertsService.error({
						        message: this.LOCALIZATION.unshareError
						    });
						}
					);
				}
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);			
	}
	
	/**
	 * Fetches the contents of the album for viewing.
	 */	
	openAlbum(album: any): void {
	    if (album.pages < 1) {
	        return;
	    } else {
			this.findAlbumRecords(album);
	    }		
	}	
	
	private findAlbumRecords(album: any): void {
		// Force the album field into the user's prefs if not already available
		let album_field = this.userService.user.properties.awfieldObjectList.filter(
			(field: any) => {
				return field.name === "album_id"
			}
		);

		if (album_field.length == 0) {
			let searchField = {
				'availableOptions': null,
				'dcName': undefined,
				'dependents': {},
				'dragdropTooltip': 'Drag to reorder fields',
				'hidden': true,
				'level': 0,
				'list': [{content: album.id + ""}],
				'name': "album_id",
				'nullMessage': " ",
				'options': null,
				'parent': undefined,
				'subType': undefined,
				'title': "Album",
				'type': "STRING"
			};
			this.userService.user.searchInputs?.push(searchField);
			this.userService.user.systemPreferences?.push({
				'defaultValue': null,
				'dependents': {},
				'displayName': "Album",
				'isAvailable': true,
				'isDefaultMetadata': false,
				'isDefaultResult': false,
				'isDefaultSearch': false,
				'isLink': false,
				'isMetadata': false,
				'isResult': false,
				'isSearch': false,
				'name': "album_id",
				'nodeName': null,
				'type': "STRING",
				'searchField': searchField
			});
			this.userService.user.properties.awfieldObjectList.push({
				'defaultValue': null,
				'displayName': "Album",
				'isAvailable': true,
				'isDefaultMetadata': false,
				'isDefaultResult': false,
				'isDefaultSearch': false,
				'isLink': false,
				'isMetadata': false,
				'isResult': false,
				'isSearch': false,
				'name': "album_id",
				'nodeName': null,
				'type': "STRING"
			});
		}

		// Search for all documents in the album
		let albumparams = {
			"q": "*",
			"criteria": 'album_id=' + album.id,
			"type": "16"
		};
		albumparams = this.searchService.validateSearchStateParams(albumparams);
		this.searchService.clearSearches(false,false);
		this.routingService.updateSearchUrl(albumparams, true, true);
	}
	
	onTabChange(event: NgbNavChangeEvent) {
		if (event.nextId === 1) {
			this.loadAlbums(1);
		} else if (event.nextId === 2) {
			this.loadSharedAlbums(1);
		}
	}		
}